"use client"

import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useRef, useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { ProjectCard } from "./project-card"

interface Project {
  _id: string;
  projectTitle: string;
  projectOverview?: string;
  featuredImage?: {
    asset: any;
    alt?: string;
  };
  client?: {
    name: string;
    industry?: {
      title: string;
    };
    logo?: {
      asset: any;
      alt?: string;
    };
  };
  services?: Array<{
    name: string;
  }>;
  tags?: Array<{
    name: string;
  }>;
  tools?: Array<{
    title: string;
  }>;
}

interface FeaturedProjectsProps {
  heading?: string;
  introText?: string;
  projects?: Project[];
}


export default function FeaturedProjects(props: FeaturedProjectsProps) {
  const {
    heading = "Recent Work",
    introText = "With 8+ years in technical project management, I specialize in overseeing web design and development projects from kickoff through QA and launch.",
    projects = []
  } = props;

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftFade, setShowLeftFade] = useState(false)
  const [showRightFade, setShowRightFade] = useState(true)

  const scrollByOneCard = (direction: 1 | -1) => {
    const el = scrollContainerRef.current
    if (!el) return
    const firstCard = el.querySelector<HTMLElement>("[data-carousel-card]")
    const gap = 24
    const step = firstCard ? firstCard.offsetWidth + gap : el.clientWidth + gap
    el.scrollBy({ left: step * direction, behavior: "smooth" })
  }

  const checkFades = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setShowLeftFade(scrollLeft > 0)
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1) // -1 for precision
    }
  }

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      checkFades() // Initial check
      scrollContainer.addEventListener("scroll", checkFades)
      return () => scrollContainer.removeEventListener("scroll", checkFades)
    }
  }, [projects])

  // If no projects, show a message
  if (!projects || projects.length === 0) {
    return (
      <section className="bg-bg py-8 contain-[layout]" aria-label="Featured projects">
        <div className="container mx-auto px-6">
          <div className="text-center py-12">
            <p className="text-text">No projects available. Please add projects in Sanity Studio.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-bg py-8 contain-[layout]" aria-label="Featured projects">
      {/* Same width as Partnerships (marquee) and Process (card-stack-half): container mx-auto px-6 */}
      <div className="container mx-auto px-6">
        <div className="mb-12">
          <p className="text-sm text-subheading tracking-wider mb-2 font-normal">FEATURED PROJECTS</p>
          <h2 className="text-4xl font-normal text-heading mb-6">{heading}</h2>
          <p className="text-text max-w-md text-base leading-relaxed">
            {introText}
          </p>
        </div>

        {/* Carousel: contained so first card aligns with header */}
        <div className="min-w-0 overflow-hidden">
          <div className="relative">
          <div
            ref={scrollContainerRef}
            role="region"
            aria-label="Featured projects carousel"
            tabIndex={0}
            className="overflow-x-auto pb-8 -mb-8 no-scrollbar scroll-smooth snap-x snap-mandatory touch-pan-x overscroll-x-contain [container-type:inline-size]"
            style={{ scrollPaddingInline: 0, WebkitOverflowScrolling: "touch" }}
          >
            <div className="flex gap-6">
              {projects.map((project, index) => {
                if (!project || typeof project !== "object") {
                  return null;
                }
                try {
                  return (
                    <div
                      key={`${project._id}-${index}`}
                      data-carousel-card
                      className="flex-shrink-0 w-[100cqw] sm:w-[320px] md:w-[375px] snap-start"
                    >
                      <ProjectCard
                        project={project}
                        className="h-[520px] sm:h-[550px] md:h-[580px]"
                      />
                    </div>
                  );
                } catch {
                  return (
                    <div
                      key={`${project._id}-${index}`}
                      data-carousel-card
                      className="flex-shrink-0 w-[100cqw] sm:w-[320px] md:w-[375px] snap-start"
                    >
                      <div className="h-[520px] sm:h-[550px] md:h-[580px] bg-bg border border-border rounded-xl p-6 flex items-center justify-center">
                        <p className="text-text">Error loading project</p>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
          {/* Left fade: start at 0 so parent overflow-hidden doesn't clip solid band; wide solid then blend (like logos) */}
          <div
            className={cn(
              "absolute inset-y-0 left-0 z-20 pointer-events-none transition-opacity hidden sm:block",
              showLeftFade ? "opacity-100" : "opacity-0",
            )}
            style={{
              width: 140,
              background: "linear-gradient(to right, var(--color-bg) 0%, var(--color-bg) 35%, transparent 100%)",
            }}
          />
          <div
            className={cn(
              "absolute inset-y-0 right-0 z-20 pointer-events-none transition-opacity hidden sm:block",
              showRightFade ? "opacity-100" : "opacity-0",
            )}
            style={{
              right: -40,
              width: 128,
              background: "linear-gradient(to left, var(--color-bg) 0%, var(--color-bg) 30%, transparent 100%)",
            }}
          />
          </div>
        </div>

        {/* Navigation: aligned with content (same width as header and logos) */}
        <div className="mt-8 flex items-center justify-end">
          <div className="flex gap-2">
            <Button
              onClick={() => scrollByOneCard(-1)}
              variant="default"
              size="icon"
              className="rounded-full"
              aria-label="Previous project"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => scrollByOneCard(1)}
              variant="default"
              size="icon"
              className="rounded-full"
              aria-label="Next project"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}
